import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ThreadData, CommentBody } from "@liveblocks/core";
import {
  useCreateThread,
  useEditThreadMetadata,
  useRoom,
  useSelf,
  useThreadSubscription,
  useThreads,
} from "@liveblocks/react";
import { useEditor, useValue } from "@tldraw/tldraw";
import { useCommentsStore } from "store/comments";
import { authorization } from "module/auth";
import { CommentPin } from "./CommentPin";
import { CommentThread } from "./CommentThread";
import { CommentComposer } from "./CommentComposer";
import { computeIsUnread } from "./unreadStatus";
import { notifyMentionsForBody } from "./notifyMentions";

// Invisible per-thread probe that reports its unread status upward. Used by
// the aggregator below to compute a global unread count that's available even
// when the side panel is collapsed.
const UnreadProbe: React.FC<{
  thread: ThreadData;
  selfId: string | undefined;
  onChange: (threadId: string, unread: boolean) => void;
}> = ({ thread, selfId, onChange }) => {
  const subscription = useThreadSubscription(thread.id);
  const localReadAt = useCommentsStore((s) => s.threadReadAt[thread.id]);
  const unread = computeIsUnread(thread, selfId, subscription, localReadAt);
  useEffect(() => {
    onChange(thread.id, unread);
  }, [thread.id, unread, onChange]);
  return null;
};

type PendingPin = {
  /** page-space coords (the bubble follows the design while the user pans) */
  x: number;
  y: number;
  shapeId?: string;
};

/**
 * The shape we author for thread metadata. Centralised so creation, lookup
 * and drag-edit stay consistent and we can drop the `as any` casts.
 */
type ThreadMetadata = {
  x: number;
  y: number;
  shapeId?: string;
};

export const CommentsOverlay: React.FC = () => {
  const editor = useEditor();
  const threadsResult = useThreads();
  const self = useSelf();
  const hydrateReadMap = useCommentsStore((s) => s.hydrateReadMap);
  const setTotalUnreadCount = useCommentsStore((s) => s.setTotalUnreadCount);

  // Load this user's local read map so unread calculations work for brand
  // new threads (not yet seen) even if Liveblocks has no subscription cursor.
  useEffect(() => {
    hydrateReadMap(self?.id || null);
  }, [self?.id, hydrateReadMap]);
  // Normalize to a concrete array — TS sees `threadsResult.threads` as
  // possibly undefined, which propagates into every downstream consumer.
  const threads: ThreadData[] = threadsResult.isLoading ? [] : threadsResult.threads ?? [];

  // Global unread aggregator: always mounted with the canvas so UI elements
  // outside the side panel (e.g. the tool island button) can show an unread
  // indicator even when the user hasn't opened the comments panel.
  const [unreadMap, setUnreadMap] = useState<Record<string, boolean>>({});
  const reportUnread = useCallback((threadId: string, unread: boolean) => {
    setUnreadMap((prev) => (prev[threadId] === unread ? prev : { ...prev, [threadId]: unread }));
  }, []);
  useEffect(() => {
    const count = Object.values(unreadMap).filter(Boolean).length;
    setTotalUnreadCount(count);
  }, [unreadMap, setTotalUnreadCount]);
  // Clear count on unmount so stale values don't linger.
  useEffect(() => () => setTotalUnreadCount(0), [setTotalUnreadCount]);
  const createThread = useCreateThread();
  const editThreadMetadata = useEditThreadMetadata();
  const room = useRoom();

  const isPlacing = useCommentsStore((s) => s.isPlacing);
  const exitPlacingMode = useCommentsStore((s) => s.exitPlacingMode);
  const togglePlacingMode = useCommentsStore((s) => s.togglePlacingMode);
  const openThreadId = useCommentsStore((s) => s.openThreadId);
  const setOpenThreadId = useCommentsStore((s) => s.setOpenThreadId);
  const showResolved = useCommentsStore((s) => s.showResolved);
  const pinsHidden = useCommentsStore((s) => s.pinsHidden);
  const setPinsHidden = useCommentsStore((s) => s.setPinsHidden);
  const togglePinsHidden = useCommentsStore((s) => s.togglePinsHidden);
  const focusThreadRequest = useCommentsStore((s) => s.focusThreadRequest);

  const [pending, setPending] = React.useState<PendingPin | null>(null);
  const pendingRef = useRef<HTMLDivElement>(null);

  // Close the new-comment composer when the user clicks anywhere outside it
  // (canvas, side panel, another bubble…). We listen on `mousedown` so the
  // dismissal feels immediate and beats any focus-stealing handlers. Clicks
  // on portaled UI (mention dropdown, emoji picker) are ignored via the
  // shared `[data-comment-portal]` attribute.
  useEffect(() => {
    if (!pending) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (pendingRef.current && pendingRef.current.contains(target)) return;
      if (target.closest("[data-comment-portal]")) return;
      setPending(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pending]);

  // Re-render when camera changes so pins follow zoom/pan.
  // Subscribe to primitive x/y/z separately so value equality is stable.
  const cameraX = useValue("camera.x", () => editor.getCamera().x, [editor]);
  const cameraY = useValue("camera.y", () => editor.getCamera().y, [editor]);
  const cameraZ = useValue("camera.z", () => editor.getCamera().z, [editor]);
  const cameraKey = `${cameraX},${cameraY},${cameraZ}`;

  const visibleThreads = useMemo(
    () => threads.filter((t) => (showResolved ? true : !t.resolved)),
    [threads, showResolved]
  );

  const getThreadViewportPoint = useCallback(
    (thread: ThreadData) => {
      const meta = thread.metadata as { x: number; y: number; shapeId?: string };
      let pagePoint = { x: meta.x, y: meta.y };
      if (meta.shapeId) {
        const shape = editor.getShape(meta.shapeId as any);
        if (shape) {
          pagePoint = { x: shape.x + meta.x, y: shape.y + meta.y };
        }
      }
      return editor.pageToViewport(pagePoint);
    },
    [editor, cameraKey]
  );

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isPlacing) return;
      // Convert screen coords (clientX/Y) to page coords
      const pagePoint = editor.screenToPage({ x: e.clientX, y: e.clientY });
      // If user clicked on a shape, anchor to it
      const shape = editor.getShapeAtPoint(pagePoint, {
        hitInside: true,
        margin: 0,
      });

      let metaX = pagePoint.x;
      let metaY = pagePoint.y;
      let shapeId: string | undefined;
      if (shape) {
        shapeId = shape.id;
        metaX = pagePoint.x - shape.x;
        metaY = pagePoint.y - shape.y;
      }

      setPending({
        x: metaX,
        y: metaY,
        shapeId,
      });
      exitPlacingMode();
    },
    [editor, isPlacing, exitPlacingMode]
  );

  // "c" toggles placing mode; ESC cancels pending or placing mode
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (pending) setPending(null);
        else if (isPlacing) exitPlacingMode();
        return;
      }
      if (e.key === "c" || e.key === "C") {
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
        e.preventDefault();
        // Guest gate: comments require an account. Mirrors the
        // `CommentsToolbarButton.handleClick` check — fires the same
        // `guest-comments-blocked` event so Tldraw.tsx opens the
        // FeatureModal teaser → signup. Without this, the `c`
        // shortcut bypasses the toolbar's gate and drops guests
        // straight into placing mode (and downstream Liveblocks
        // comments calls that assume an authed user).
        if (!authorization.getUser?.()?.id) {
          window.dispatchEvent(new CustomEvent("guest-comments-blocked"));
          return;
        }
        if (pending) setPending(null);
        // Shift+C always toggles pin visibility. Plain C: if pins are hidden,
        // un-hide them (without entering placing mode); otherwise toggle the
        // Figma-style placing mode.
        if (e.shiftKey) {
          togglePinsHidden();
          return;
        }
        // If pins are hidden, reveal them AND enter placing mode.
        if (pinsHidden) setPinsHidden(false);
        togglePlacingMode();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending, isPlacing, exitPlacingMode, togglePlacingMode, pinsHidden, togglePinsHidden, setPinsHidden]);

  const handleCreateThread = useCallback(
    async (body: CommentBody, files: File[]) => {
      if (!pending) return;
      // Upload any user-attached images to Liveblocks first. We do this in
      // parallel and surface any failed upload by skipping it — partial
      // attachments are still better than aborting the whole thread create.
      const attachments = files.length
        ? (
            await Promise.all(
              files.map(async (file) => {
                try {
                  const local = room.prepareAttachment(file);
                  return await room.uploadAttachment(local);
                } catch (err) {
                  console.error("[comments] attachment upload failed", err);
                  return null;
                }
              })
            )
          ).filter((a): a is NonNullable<typeof a> => a !== null)
        : [];
      const thread = await createThread({
        body,
        // Only include `attachments` when we actually have some — passing an
        // empty array can trigger feature-gating on Liveblocks projects where
        // attachments aren't enabled, which would reject the whole request.
        ...(attachments.length ? { attachments } : {}),
        metadata: {
          x: pending.x,
          y: pending.y,
          ...(pending.shapeId ? { shapeId: pending.shapeId } : {}),
        },
      });
      setPending(null);
      if (thread?.id) setOpenThreadId(thread.id);
      // Fire-and-forget: email anyone @-mentioned in this first comment.
      if (self) {
        void notifyMentionsForBody(
          body,
          {
            id: self.id,
            name: (self.info as any)?.name || "An Oppizi user",
            avatar: (self.info as any)?.avatar || "",
            color: (self.info as any)?.color || "",
          },
          thread?.id
        );
      }
    },
    [pending, createThread, room, setOpenThreadId, self]
  );

  const openThread = useMemo(
    () => visibleThreads.find((t) => t.id === openThreadId) || null,
    [visibleThreads, openThreadId]
  );

  // Keep the latest threads list in a ref so the focus effect can read it
  // without re-running every time a thread is updated.
  const threadsRef = useRef(threads);
  useEffect(() => {
    threadsRef.current = threads;
  }, [threads]);

  // When the sidebar requests focus on a thread, pan the camera to center it.
  useEffect(() => {
    if (!focusThreadRequest) return;
    const thread = threadsRef.current.find((t) => t.id === focusThreadRequest.threadId);
    if (!thread) return;
    const meta = thread.metadata as { x: number; y: number; shapeId?: string };
    let pagePoint = { x: meta.x, y: meta.y };
    if (meta.shapeId) {
      const shape = editor.getShape(meta.shapeId as any);
      if (shape) pagePoint = { x: shape.x + meta.x, y: shape.y + meta.y };
    }
    try {
      editor.centerOnPoint(pagePoint, { animation: { duration: 320 } });
    } catch {
      // Older tldraw versions: fallback to setCamera
      const { z } = editor.getCamera();
      const vp = editor.getViewportScreenBounds();
      editor.setCamera({
        x: -pagePoint.x + vp.w / 2 / z,
        y: -pagePoint.y + vp.h / 2 / z,
        z,
      });
    }
  }, [focusThreadRequest, editor]);

  // Pin drag-and-drop: only the thread author may reposition their bubble.
  // We use a small movement threshold so a normal click still opens the
  // thread; only a sustained drag commits a metadata edit.
  const DRAG_THRESHOLD_PX = 4;
  const [dragState, setDragState] = useState<{
    threadId: string;
    viewportPoint: { x: number; y: number };
    started: boolean;
  } | null>(null);
  const dragRef = useRef<{
    threadId: string;
    pointerId: number;
    startScreen: { x: number; y: number };
    startViewport: { x: number; y: number };
    started: boolean;
  } | null>(null);
  // Latches true for a single click event after a drag finishes — without it
  // pointerup → click would re-toggle the thread popover the user just moved.
  const justDraggedRef = useRef(false);

  const beginPinDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, thread: ThreadData, currentViewport: { x: number; y: number }) => {
      // Author-only — guard at the call site too, but be defensive here.
      if (!self?.id || self.id !== thread.comments[0]?.userId) return;
      // Only primary button (left mouse / single touch).
      if (e.button !== 0 && e.pointerType === "mouse") return;
      dragRef.current = {
        threadId: thread.id,
        pointerId: e.pointerId,
        startScreen: { x: e.clientX, y: e.clientY },
        startViewport: currentViewport,
        started: false,
      };
      // Don't `setPointerCapture` yet — capturing on the wrapper can swallow
      // the synthesized `click` event on the inner pin button when the user
      // never actually moved (= a normal click). We capture lazily inside
      // the move handler the first time the drag threshold is crossed.
    },
    [self?.id]
  );

  const handlePinPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startScreen.x;
    const dy = e.clientY - d.startScreen.y;
    if (!d.started && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    if (!d.started) {
      d.started = true;
      // Now that the user committed to a drag, capture so leaving the pin's
      // bounds doesn't drop the gesture mid-move.
      try {
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
    }
    setDragState({
      threadId: d.threadId,
      viewportPoint: { x: d.startViewport.x + dx, y: d.startViewport.y + dy },
      started: true,
    });
  }, []);

  const endPinDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = dragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      const wasDrag = d.started;
      const finalScreen = { x: e.clientX, y: e.clientY };
      dragRef.current = null;
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      setDragState(null);
      if (!wasDrag) return; // treat as click — pin's onClick handler will fire
      justDraggedRef.current = true;
      // Browsers don't always dispatch a `click` after a drag (especially
      // post-`setPointerCapture`), so the latch could stay true until the
      // next user click and accidentally swallow it. Clear on the next tick
      // — the synchronous post-pointerup click (if any) reads it first.
      setTimeout(() => {
        justDraggedRef.current = false;
      }, 0);
      // Convert final screen position to a fresh page-space coord (and
      // rebind to whatever shape sits underneath, so the pin keeps tracking
      // a moved/resized shape — same convention as creation).
      const pagePoint = editor.screenToPage(finalScreen);
      const shape = editor.getShapeAtPoint(pagePoint, { hitInside: true, margin: 0 });
      let metaX = pagePoint.x;
      let metaY = pagePoint.y;
      let shapeId: string | undefined;
      if (shape) {
        shapeId = shape.id as string;
        metaX = pagePoint.x - shape.x;
        metaY = pagePoint.y - shape.y;
      }
      // Preserve any existing metadata fields we don't manage here (the
      // Liveblocks room metadata type may carry additional keys we don't
      // know about) by spreading the current thread metadata first.
      const dragged = threads.find((t) => t.id === d.threadId);
      const baseMeta = (dragged?.metadata as ThreadMetadata | undefined) ?? ({} as ThreadMetadata);
      const next: ThreadMetadata = {
        ...baseMeta,
        x: metaX,
        y: metaY,
        // Reset shapeId — we either set it to the new anchor or clear it
        // when the drop point isn't over a shape.
        shapeId: shapeId ?? undefined,
      };
      editThreadMetadata({ threadId: d.threadId, metadata: next });
    },
    [editor, editThreadMetadata, threads]
  );

  const cursorStyle: React.CSSProperties | undefined = isPlacing
    ? { cursor: `url("/cursors/comment.svg") 5 18, pointer` }
    : undefined;

  return (
    <div
      className={`absolute inset-0 ${isPlacing ? "pointer-events-auto" : "pointer-events-none"}`}
      // `isolation: isolate` + low zIndex traps every internal pin/popover
      // z-index (1, 50, 60) inside this overlay's stacking context. Without
      // it those children leak out and paint over sibling chrome (the left
      // sidebar with the tabs sits at zIndex:10 — the popovers' z-60 was
      // winning when a bubble drifted under the sidebar after a pan).
      style={{ ...cursorStyle, isolation: "isolate", zIndex: 1 }}
      onClick={isPlacing ? handleCanvasClick : undefined}
      data-comments-overlay
    >
      {/* Invisible per-thread unread probes — feed the global unread count. */}
      {threads.map((t) => (
        <UnreadProbe key={`probe-${t.id}`} thread={t} selfId={self?.id} onChange={reportUnread} />
      ))}

      {!pinsHidden &&
        visibleThreads.map((thread) => {
          const draggedPoint = dragState && dragState.threadId === thread.id ? dragState.viewportPoint : null;
          const p = draggedPoint ?? getThreadViewportPoint(thread);
          const isOpen = openThread?.id === thread.id;
          const isAuthor = !!self?.id && self.id === thread.comments[0]?.userId;
          // Flip popover if pin is near right/bottom edge of viewport
          const vpWidth = window.innerWidth;
          const vpHeight = window.innerHeight;
          const flipX = p.x > vpWidth - 360;
          const flipY = p.y > vpHeight - 280;
          // 5px gap between pin edge and popover. Pin is 28×28 anchored at
          // the parent's top-left, so:
          //  - non-flipped: popover.left = 28 + 5 = 33
          //  - flipped:     popover.right = 28 + 5 = 33 (right offset measures
          //    from the parent's right edge, so we need the same +5 past the
          //    pin's near edge — using 23 here would land *inside* the pin).
          // Shift the popover 15px higher than the pin so the first comment
          // sits above the bubble's vertical center instead of below it.
          const placement = `${flipX ? "right-[33px]" : "left-[33px]"} ${flipY ? "bottom-[15px]" : "-top-[15px]"}`;

          const isDragging = dragState?.threadId === thread.id && dragState.started;
          return (
            <div
              key={thread.id}
              className="pointer-events-auto absolute"
              style={{
                transform: `translate3d(${p.x}px, ${p.y}px, 0)`,
                zIndex: isOpen || isDragging ? 50 : 1,
              }}
            >
              {/* Drag handlers + grab cursor are scoped to JUST the pin —
                  not the popover — so opening the thread and selecting text
                  inside it doesn't show a grab cursor or hijack pointer
                  events. */}
              <div
                style={{
                  cursor: isAuthor ? (isDragging ? "grabbing" : "grab") : undefined,
                  touchAction: isAuthor ? "none" : undefined,
                }}
                // Capture-phase: CommentPin's `onPointerDown` calls
                // stopPropagation to keep tldraw from grabbing the event,
                // which would otherwise also block this bubble-phase listener.
                onPointerDownCapture={isAuthor ? (e) => beginPinDrag(e, thread, p) : undefined}
                onPointerMove={isAuthor ? handlePinPointerMove : undefined}
                onPointerUp={isAuthor ? endPinDrag : undefined}
                onPointerCancel={isAuthor ? endPinDrag : undefined}
              >
                <CommentPin
                  thread={thread}
                  isOpen={isOpen}
                  onClick={(e) => {
                    // If the pointerup was actually a drag, swallow the click —
                    // the drag commit already ran in `endPinDrag`.
                    e.stopPropagation();
                    if (justDraggedRef.current) {
                      justDraggedRef.current = false;
                      return;
                    }
                    setOpenThreadId(isOpen ? null : thread.id);
                  }}
                />
              </div>
              {isOpen && (
                <CommentThread thread={thread} onClose={() => setOpenThreadId(null)} placementClassName={placement} />
              )}
            </div>
          );
        })}

      {pending &&
        (() => {
          // Anchor the new-comment bubble to its page-space point — when the
          // user pans/zooms while the composer is open, it should travel with
          // the design just like any persisted bubble would.
          const pendingPagePoint = pending.shapeId
            ? (() => {
                const shape = editor.getShape(pending.shapeId as any);
                return shape ? { x: shape.x + pending.x, y: shape.y + pending.y } : { x: pending.x, y: pending.y };
              })()
            : { x: pending.x, y: pending.y };
          // `cameraKey` (read above) makes this re-evaluate on camera change.
          void cameraKey;
          const pendingVp = editor.pageToViewport(pendingPagePoint);
          return (
            <div
              ref={pendingRef}
              className="pointer-events-auto absolute"
              style={{
                transform: `translate3d(${pendingVp.x}px, ${pendingVp.y}px, 0)`,
                // Sit above every other pin (1) and any open thread popover (50).
                // Without this, an existing bubble dropped on the same area
                // would render on top of the new-comment composer.
                zIndex: 60,
              }}
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EE4360] text-[11px] font-semibold text-white shadow-[0_2px_8px_rgba(16,24,40,0.2)] ring-2 ring-white">
                +
              </div>
              <div className="absolute left-[33px] -top-[15px] z-40 w-[320px] rounded-xl border border-[#e2e2fc] bg-white p-3 shadow-[0_12px_32px_rgba(16,24,40,0.12)]">
                <CommentComposer
                  autoFocus
                  compact
                  placeholder="Add a comment…"
                  submitLabel="Comment"
                  onSubmit={handleCreateThread}
                  onCancel={() => setPending(null)}
                />
              </div>
            </div>
          );
        })()}
    </div>
  );
};
